// src/pages/EmailApproval.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FiCheckCircle, FiXCircle, FiLoader } from 'react-icons/fi';

const EmailApproval = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const processApproval = async () => {
      setLoading(true);
      
      try {
        // Determine action from URL
        const isApprove = location.pathname.includes('/approve/');
        const isReject = location.pathname.includes('/reject/');
        const isView = location.pathname.includes('/view/');
        
        if (isView) {
          // View leave details
          const response = await fetch(`/api/leave/email/view/${token}/`);
          const data = await response.json();
          setResult({ type: 'view', data });
          setLoading(false);
          return;
        }
        
        const action = isApprove ? 'approve' : 'reject';
        const apiUrl = `/api/leave/email/${action}/${token}/`;
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        const data = await response.json();
        
        if (response.ok) {
          setResult({ type: action, success: true, data });
        } else {
          setResult({ type: action, success: false, error: data.error || 'Failed to process request' });
        }
      } catch (err) {
        setError(err.message || 'Network error');
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      processApproval();
    }
  }, [token, location]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <FiLoader className="w-12 h-12 text-indigo-600 mx-auto animate-spin" />
          <p className="mt-4 text-gray-600">Processing your request...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
          <FiXCircle className="w-16 h-16 text-red-500 mx-auto" />
          <h2 className="text-xl font-bold text-gray-800 mt-4">Something Went Wrong</h2>
          <p className="text-gray-600 mt-2">{error}</p>
          <button
            onClick={() => window.location.href = '/'}
            className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (result?.type === 'view') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full p-8">
          <h2 className="text-2xl font-bold text-gray-800">Leave Request Details</h2>
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Employee</p>
                <p className="font-medium">{result.data.employee_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Employee ID</p>
                <p className="font-medium">{result.data.employee_id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Leave Type</p>
                <p className="font-medium">{result.data.leave_type_display}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="font-medium">{result.data.status_display}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Start Date</p>
                <p className="font-medium">{result.data.start_date}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">End Date</p>
                <p className="font-medium">{result.data.end_date}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-500">Reason</p>
                <p className="font-medium">{result.data.reason}</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => window.location.href = '/'}
            className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
        {result?.success ? (
          <>
            <FiCheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <h2 className="text-2xl font-bold text-gray-800 mt-4">
              {result.type === 'approve' ? 'Leave Approved!' : 'Leave Rejected!'}
            </h2>
            <p className="text-gray-600 mt-2">
              {result.type === 'approve' 
                ? 'The leave request has been approved successfully.' 
                : 'The leave request has been rejected successfully.'}
            </p>
            {result.data?.leave_id && (
              <p className="text-sm text-gray-500 mt-2">Request ID: #{result.data.leave_id}</p>
            )}
          </>
        ) : (
          <>
            <FiXCircle className="w-16 h-16 text-red-500 mx-auto" />
            <h2 className="text-xl font-bold text-gray-800 mt-4">Unable to Process</h2>
            <p className="text-gray-600 mt-2">{result?.error || 'This link may have expired or already been used.'}</p>
          </>
        )}
        <button
          onClick={() => window.location.href = '/'}
          className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
};

export default EmailApproval;